import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock, FileText, MoreHorizontal, Plus, Search } from "lucide-react";
import { formatDate } from "../api";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { useBoardDrag, type CardOrigin, type DropTarget } from "../components/crm/board-drag";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { PageHeader } from "../components/Ui";
import Dialog from "../components/Dialog";
import RecordForm, { type Field } from "../components/RecordForm";
import {
  ActionForm,
  DataTable,
  NoClient,
  RequestView,
  label,
  useMutation,
  type RecordData,
} from "../components/Workflow";

// The pipeline's columns, in order (Neil, 2026-10-09: "a pipeline, just like in Pyre OS, Winning
// Team and Radar"). The stored statuses stay as they were; the names say where the content is.
// "Archived" is a stage too, but it has no column.
const STAGES = ["idea", "approved", "in_progress", "produced", "published"];
const STATUSES = [...STAGES, "archived"];
const STAGE_NAMES: Record<string, string> = { idea: "Ideas", approved: "Approved", in_progress: "In production", produced: "Ready to post", published: "Posted", archived: "Archived" };
const stageName = (s: unknown) => STAGE_NAMES[String(s)] || label(String(s || ""));
const IDEA_TYPES = ["general", "ad_concept", "gbp_post", "social_post", "blog_topic", "email_concept", "video_concept", "seasonal_campaign", "offer_idea", "content_series"];
const PRIORITIES = ["high", "medium", "low"];
// The platforms Generate with AI uses (routes/content_ideas.js); a platform typed by hand earlier stays selectable.
const PLATFORMS = ["google_ads", "gbp", "facebook", "instagram", "tiktok", "youtube", "linkedin", "email", "website", "sms"];

/** A stored code reads as a name ("gbp" → "GBP"); a platform someone typed ("Nextdoor") stays as typed. */
const platformName = (value: unknown) => {
  const raw = String(value || "");
  return /^[a-z0-9_]+$/.test(raw) ? label(raw) : raw;
};

/** One form for adding and editing an idea; editing adds the stage. */
function ideaFields(row?: RecordData): Field[] {
  const current = String(row?.platform || "");
  const platforms = ["", ...PLATFORMS, ...(current && !PLATFORMS.includes(current) ? [current] : [])];
  const fields: Field[] = [
    { key: "title", label: "Idea", required: true, placeholder: "Spring cleanout special" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "idea_type", label: "Type", type: "select", options: IDEA_TYPES, half: true },
    { key: "priority", label: "Priority", type: "select", options: PRIORITIES, default: "medium", half: true },
    { key: "platform", label: "Platform", type: "select", options: platforms, optionLabel: (v) => (v ? platformName(v) : "Not set") },
    { key: "notes", label: "Notes", type: "textarea" },
  ];
  if (row) fields.push({ key: "status", label: "Stage", type: "select", options: STATUSES, optionLabel: stageName });
  return fields;
}

const blankFilters = { text: "", type: "", platform: "", priority: "", aiOnly: false };

function IdeasWorkspace({ clientId, clientName }: { clientId: number; clientName: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "archived" ? "archived" : params.get("view") === "list" ? "list" : "board";
  const ideas = useApi<RecordData[]>(`/api/content-ideas/${clientId}`);
  const mutation = useMutation();
  const [dialog, setDialog] = useState<"add" | "generate" | null>(null);
  const [filters, setFilters] = useState(blankFilters);
  // "Write it with AI" answers with the draft's link, which a plain notice cannot carry.
  const [written, setWritten] = useState<{ title: string; url: string; status: string; existing?: boolean } | null>(null);
  const [writingTitle, setWritingTitle] = useState<string | null>(null);
  const changing = useRef(false);

  const all = ideas.data || [];
  const recordId = params.get("record");
  const open = recordId ? all.find((idea) => String(idea.id) === recordId) : undefined;
  // The view and an open idea live in the URL: a reload keeps them, a closed idea never reopens.
  const setParam = (key: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });

  const needle = filters.text.trim().toLowerCase();
  const archivedCount = all.filter(idea => idea.status === "archived").length;
  const visibleIdeas = all.filter(idea => view === "archived" ? idea.status === "archived" : idea.status !== "archived");
  const filtered = visibleIdeas.filter((idea) =>
    (!filters.type || idea.idea_type === filters.type) &&
    (!filters.platform || String(idea.platform || "") === filters.platform) &&
    (!filters.priority || idea.priority === filters.priority) &&
    (!filters.aiOnly || idea.ai_generated === true) &&
    (!needle || `${idea.title || ""} ${idea.description || ""}`.toLowerCase().includes(needle)),
  );
  const choices = (key: string) => [...new Set(visibleIdeas.map((idea) => String(idea[key] || "")).filter(Boolean))].sort();
  const filtering = JSON.stringify(filters) !== JSON.stringify(blankFilters);

  async function moveTo(idea: RecordData, status: string) {
    if (idea.status === status || changing.current) return;
    changing.current = true;
    // The card moves at once; a refused save puts it back.
    ideas.setData((rows) => rows?.map((row) => (row.id === idea.id ? { ...row, status } : row)) ?? rows);
    const done = await mutation.run(`/api/content-ideas/${clientId}/${idea.id}`, { status }, "PUT");
    if (!done) ideas.reload();
    else mutation.setNotice(`Moved to ${stageName(status)}.`);
    changing.current = false;
  }
  async function writeIt(idea: RecordData) {
    if (changing.current) return;
    changing.current = true;
    setWritten(null);
    setWritingTitle(String(idea.title));
    const r = await mutation.run<{ url: string; status: string; existing?: boolean }>(`/api/content-ideas/${clientId}/${idea.id}/write`, {});
    changing.current = false;
    setWritingTitle(null);
    if (!r) return;
    ideas.reload();
    setWritten({ title: String(idea.title), url: r.url, status: r.status, existing: r.existing });
  }

  const headerActions = (
    <div className="workflow-actions">
      <button className="button secondary" disabled={mutation.busy} onClick={() => setDialog("generate")}>Generate with AI</button>
      <button className="button" disabled={mutation.busy} onClick={() => setDialog("add")}>Add idea</button>
    </div>
  );
  const analystPrompt = "Suggest 5 specific content ideas for this client. For each one give the type, the platform, a priority (high, medium or low) and a short description.";

  return (
    <div className="ideas-page">
      <PageHeader title="Content pipeline" description={`${clientName}'s content, from idea to posted. Drag a card to move it.`} actions={headerActions} />
      {!dialog && mutation.feedback}
      {writingTitle && <p className="inline-notice" role="status">Writing a draft for “{writingTitle}”… This can take a minute.</p>}
      {written && (
        <p className="inline-notice success" role="status">
          <span>{written.existing ? "The existing draft" : "The draft"} for "{written.title}" is in Content review. The idea is in {stageName(written.status)}.</span>
          <Link className="text-button" to={written.url}>Open it</Link>
          <button type="button" aria-label="Dismiss" onClick={() => setWritten(null)}>×</button>
        </p>
      )}
      <RequestView request={ideas}>
        {!all.length ? (
          // The pipeline shows even when empty, so its columns explain what happens next.
          <>
            <p className="inline-notice content-pipeline-empty">
              <span>No content for {clientName} yet. Press Generate with AI, Add idea, or save a post from <Link to={`/viral?client_id=${clientId}`}>Viral posts</Link>.</span>
            </p>
            <PipelineBoard ideas={[]} all={[]} busy={mutation.busy} onOpen={() => {}} onMove={() => {}} onWrite={() => {}} onAdd={() => setDialog("add")} />
          </>
        ) : (
          <>
            <div className="portfolio-controls content-filters">
              <div className="segmented-control compact" role="group" aria-label="View">
                {(["board", "list", "archived"] as const).map((v) => (
                  <button key={v} type="button" className={view === v ? "active" : ""} aria-pressed={view === v} onClick={() => setParam("view", v === "board" ? null : v)}>
                    {v === "board" ? "Board" : v === "list" ? "List" : `Archived${archivedCount ? ` (${archivedCount})` : ""}`}
                  </button>
                ))}
              </div>
              <label className="search-control ideas-search">
                <Search aria-hidden="true" />
                <span className="sr-only">Search ideas</span>
                <input type="search" value={filters.text} placeholder="Search ideas" onChange={(event) => setFilters({ ...filters, text: event.target.value })} />
              </label>
              {([["Type", "type", "idea_type"], ["Platform", "platform", "platform"], ["Priority", "priority", "priority"]] as const).map(([name, filterKey, field]) => (
                <label key={name} className="select-control">
                  <span className="control-prefix">{name}</span>
                  <select aria-label={`Filter by ${name.toLowerCase()}`} value={filters[filterKey]} onChange={(event) => setFilters({ ...filters, [filterKey]: event.target.value })}>
                    <option value="">All</option>
                    {choices(field).map((option) => <option key={option} value={option}>{field === "platform" ? platformName(option) : label(option)}</option>)}
                  </select>
                </label>
              ))}
              <label className="checkbox-line">
                <input type="checkbox" checked={filters.aiOnly} onChange={(event) => setFilters({ ...filters, aiOnly: event.target.checked })} />
                AI-generated only
              </label>
              {filtering && <button type="button" className="text-button" onClick={() => setFilters(blankFilters)}>Clear filters</button>}
              <span className="muted">{filtering ? `${filtered.length.toLocaleString()} of ${visibleIdeas.length.toLocaleString()}` : `${visibleIdeas.length.toLocaleString()} ${visibleIdeas.length === 1 ? "idea" : "ideas"}`}</span>
            </div>
            {view === "board" && filtering && !filtered.length && <p className="inline-notice">No ideas match these filters. <button type="button" className="text-button" onClick={() => setFilters(blankFilters)}>Clear filters</button></p>}
            {view === "board" ? (
              <PipelineBoard
                ideas={filtered}
                all={all}
                busy={mutation.busy}
                onOpen={(idea) => { if (!mutation.busy) setParam("record", String(idea.id)); }}
                onMove={(idea, status) => void moveTo(idea, status)}
                onWrite={(idea) => void writeIt(idea)}
                onAdd={() => setDialog("add")}
              />
            ) : (
              <section className="panel ideas-list">
                <DataTable
                  pageSize={50}
                  empty={view === "archived" && !filtering ? "No archived ideas." : "No ideas match these filters."}
                  columns={["idea", "type", "platform", "priority", "status", "draft", "added"]}
                  rows={filtered.map((idea) => ({
                    id: idea.id,
                    idea: <button type="button" className="record-open ideas-open" disabled={mutation.busy} onClick={() => setParam("record", String(idea.id))}>{idea.title}</button>,
                    type: label(String(idea.idea_type || "general")),
                    platform: idea.platform ? <>{platformName(idea.platform)}</> : null,
                    priority: idea.priority,
                    status: stageName(idea.status),
                    draft: draftLink(idea) ? <Link to={draftLink(idea)!}>Open draft</Link> : canWrite(idea) ? <button type="button" className="text-button" disabled={mutation.busy} onClick={() => void writeIt(idea)}>Write with AI</button> : null,
                    added: <>{formatDate(idea.created_at)}</>,
                  }))}
                />
              </section>
            )}
          </>
        )}
      </RequestView>
      {open && (
        <RecordForm
          protectChanges
          key={String(open.id)}
          title="Edit idea"
          description={`Added ${formatDate(open.created_at)}${open.submitted_by_name ? ` by ${open.submitted_by_name}` : ""}${open.ai_generated ? ", written by AI" : ""}.`}
          endpoint={`/api/content-ideas/${clientId}/${open.id}`}
          method="PUT"
          fields={ideaFields(open)}
          initial={open}
          onClose={() => setParam("record", null)}
          onSaved={ideas.reload}
        />
      )}
      {dialog === "add" && (
        <RecordForm
          protectChanges
          title="Add idea"
          endpoint={`/api/content-ideas/${clientId}`}
          fields={ideaFields()}
          submitLabel="Add idea"
          onClose={() => setDialog(null)}
          onSaved={() => { ideas.reload(); mutation.setNotice("Idea added."); }}
        />
      )}
      {dialog === "generate" && (
        <Dialog title="Generate ideas with AI" description="New ideas go straight onto the board, each with a hook. They draw on the brand profile, the best recent Facebook posts and competitor outliers." onClose={() => { if (!mutation.busy) setDialog(null); }}>
          <ActionForm
            submit="Generate ideas"
            busyLabel="Generating…"
            busy={mutation.busy}
            onCancel={() => setDialog(null)}
            fields={[
              { name: "prompt_context", label: "What should the ideas focus on?", type: "textarea", placeholder: "Spring cleanouts, a new service area, a slow month" },
              { name: "count", label: "Number of ideas", type: "number", min: 1, max: 10, value: 5 },
            ]}
            onSubmit={async (data) => {
              const result = await mutation.run<{ added: number }>(`/api/content-ideas/${clientId}/generate`, data);
              if (!result) return;
              setDialog(null);
              ideas.reload();
              mutation.setNotice(`${result.added} ${result.added === 1 ? "idea" : "ideas"} added to the board.`);
            }}
          />
          {mutation.feedback}
          <button type="button" className="text-button ideas-analyst-link" disabled={mutation.busy} onClick={() => navigate(`/analyst?client_id=${clientId}`, { state: { analystDraft: analystPrompt } })}>
            Write the prompt in the AI analyst instead
          </button>
        </Dialog>
      )}
    </div>
  );
}

/** The draft an idea's "Write it" made, from the line it left in the notes. */
const draftLink = (idea: RecordData) => [...String(idea.notes || "").matchAll(/^Draft in Content review: (\/content\?client_id=(\d+)&record=\d+)[ \t]*$/gm)].reverse().find(match => Number(match[2]) === Number(idea.client_id))?.[1] || null;
/** The hook "Generate with AI" and "Save as idea" put at the top of the description. */
const hookOf = (idea: RecordData) => /^Hook: (.+)$/m.exec(String(idea.description || ""))?.[1] || null;
const ageOf = (iso: unknown) => {
  const days = Math.floor((Date.now() - Date.parse(String(iso))) / 86400000);
  return !Number.isFinite(days) ? "" : days < 1 ? "today" : days < 7 ? `${days}d` : days < 60 ? `${Math.floor(days / 7)}w` : `${Math.floor(days / 30)}mo`;
};
// Blogs and emails have no draft type in Content review yet (utils/ideaWriter.js draftTypeFor).
const canWrite = (idea: RecordData) => !["archived", "published"].includes(String(idea.status)) && !["blog_topic", "email_concept"].includes(String(idea.idea_type)) && !["email", "website"].includes(String(idea.platform));

/**
 * The content pipeline: the CRM pipeline's board (Orbit look, Winning Team OS pointer drag,
 * components/crm/board-drag.ts) with content cards. A card opens on click, moves by drag or by
 * its menu (phones, keyboards), and its menu can have the AI write the draft.
 */
function PipelineBoard({ ideas, all, busy, onOpen, onMove, onWrite, onAdd }: {
  ideas: RecordData[]; all: RecordData[]; busy: boolean;
  onOpen: (idea: RecordData) => void; onMove: (idea: RecordData, status: string) => void;
  onWrite: (idea: RecordData) => void; onAdd: () => void;
}) {
  const boardRef = useRef<HTMLDivElement | null>(null);
  const byId = (id: number) => all.find((x) => Number(x.id) === id);
  const { drag, ghostRef, startDrag, consumeClick } = useBoardDrag({
    scrollerRef: boardRef,
    onDrop: (origin: CardOrigin, target: DropTarget) => { const idea = byId(origin.dealId); if (idea && target.stageId !== idea.status) onMove(idea, target.stageId); },
    onTap: (origin) => { const idea = byId(origin.dealId); if (idea) onOpen(idea); },
  });
  const dragging = drag ? byId(drag.dealId) || null : null;
  const menu = (idea: RecordData) => (
    <span className="crm-kcard-move" data-no-drag onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <QuickMenu label={`Actions for ${idea.title}`} disabled={busy} align="right" trigger={<span className="crm-kcard-move-btn" title="Write it or move it"><MoreHorizontal aria-hidden="true" /></span>}>
        {(close) => (
          <>
            {canWrite(idea) && !draftLink(idea) && <QuickMenuItem onClick={() => { close(); onWrite(idea); }}>Write it with AI</QuickMenuItem>}
            {STAGES.map((s) => <QuickMenuItem key={s} active={s === idea.status} onClick={() => { close(); onMove(idea, s); }}>Move to {stageName(s)}</QuickMenuItem>)}
            <QuickMenuItem onClick={() => { close(); onMove(idea, "archived"); }}>Archive idea</QuickMenuItem>
          </>
        )}
      </QuickMenu>
    </span>
  );
  return (
    <>
      <div className="crm-kanban content-pipeline" ref={boardRef} aria-label="Content pipeline" aria-busy={busy || undefined}>
        {STAGES.map((stage) => {
          const items = ideas.filter((idea) => idea.status === stage);
          const visible = drag ? items.filter((idea) => Number(idea.id) !== drag.dealId) : items;
          const slot = drag && drag.target.stageId === stage ? Math.max(0, Math.min(drag.target.index, visible.length)) : null;
          const placeholder = slot !== null && drag ? <div className="crm-kslot" aria-hidden="true" style={{ height: drag.height }} /> : null;
          return (
            <section key={stage} className={`crm-kcol${slot !== null ? " over" : ""}`} data-stage-id={stage} aria-label={`${stageName(stage)}, ${items.length}`}>
              <header className="crm-kcol-head">
                <span className={`crm-kpill c-${stage}`}><span className="crm-dot" aria-hidden="true" />{stageName(stage)}</span>
                <span className="crm-kcount">{items.length}</span>
              </header>
              <div className="crm-kcol-body" data-col-body>
                {visible.map((idea, i) => (
                  <Slot key={String(idea.id)} before={slot === i ? placeholder : null}>
                    <IdeaCard
                      idea={idea} menu={menu(idea)}
                      onPointerDown={(e) => { if (!busy) startDrag(e, { dealId: Number(idea.id), stageId: stage, index: i }); }}
                      onOpen={() => { if (!consumeClick()) onOpen(idea); }}
                    />
                  </Slot>
                ))}
                {slot === visible.length ? placeholder : null}
                {!items.length && slot === null && <p className="crm-kempty">Nothing here</p>}
                {stage === "idea" && <button type="button" className="crm-kadd" disabled={busy} onClick={onAdd}><Plus aria-hidden="true" />Add idea</button>}
              </div>
            </section>
          );
        })}
      </div>
      {/* The card under the cursor, on the body so no column's overflow can clip it. */}
      {drag && dragging && createPortal(
        <div ref={ghostRef} className="crm-kghost" style={{ width: drag.width, transform: `translate3d(${drag.originX}px, ${drag.originY}px, 0) rotate(1.5deg)` }}>
          <IdeaCard idea={dragging} ghost />
        </div>,
        document.body,
      )}
    </>
  );
}

function Slot({ before, children }: { before: ReactNode; children: ReactNode }) {
  return <>{before}{children}</>;
}

/** One piece of content on the pipeline; the drag ghost draws the same card. */
function IdeaCard({ idea, ghost, menu, onPointerDown, onOpen }: {
  idea: RecordData; ghost?: boolean; menu?: ReactNode;
  onPointerDown?: (e: React.PointerEvent<HTMLElement>) => void; onOpen?: () => void;
}) {
  const hook = hookOf(idea);
  const draft = draftLink(idea);
  return (
    <article
      className={`crm-kcard content-kcard${ghost ? " is-ghost" : ""}${ghost ? "" : " can-drag"}`}
      data-deal-id={ghost ? undefined : Number(idea.id)}
      onPointerDown={onPointerDown}
      onClick={onOpen}
      onKeyDown={ghost ? undefined : (e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen?.(); } }}
      onDragStart={(e) => e.preventDefault()}
      tabIndex={ghost ? undefined : 0}
      aria-label={ghost ? undefined : `${idea.title}, ${stageName(idea.status)}`}
      aria-hidden={ghost || undefined}
    >
      <p className="crm-kcard-title">{String(idea.title)}</p>
      {hook && <p className="content-kcard-hook">{hook}</p>}
      <p className="content-kcard-meta">
        {[label(String(idea.idea_type || "general")), idea.platform ? platformName(idea.platform) : null].filter(Boolean).join(" · ")}
        {idea.priority === "high" && <span className="content-kcard-high">High</span>}
      </p>
      <div className="crm-kcard-foot">
        {draft && !ghost
          ? <Link className="content-kcard-draft" to={draft} data-no-drag onClick={(e) => e.stopPropagation()}><FileText aria-hidden="true" />Draft</Link>
          : <span />}
        <span className="crm-kcard-age" title={`Added ${formatDate(idea.created_at)}`}><Clock aria-hidden="true" />{ageOf(idea.created_at)}</span>
        {!ghost && menu}
      </div>
    </article>
  );
}

export default function ContentWorkspace() {
  const { clientId, client } = useClientSelection();
  return clientId ? (
    <IdeasWorkspace key={clientId} clientId={clientId} clientName={client?.name || "this client"} />
  ) : (
    <>
      <PageHeader title="Content pipeline" />
      <NoClient />
    </>
  );
}
